import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Box, Typography, TableHead, TableCell, Table, TableBody, TableRow, TablePagination } from '@mui/material';
import DeleteDialog from './delete_dialog.tsx';
import { Dossier, fetchDossier, fetchDossierReports, removeReportFromDossier, Report } from '../../../services/stixify_api.ts';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getDateString } from '../../../services/utils.ts';
import { URLS } from '../../../services/urls.ts';
import { ConfirmDeleteDialog } from './confirm-delete.tsx';
import { TeamRouteContext } from '../../team-layout.tsx/index.tsx';


const DossierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [openRemoveReportDialog, setOpenRemoveReportDialog] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [page, setPage] = useState<number>(0); // Current page
  const [pageSize, setPageSize] = useState<number>(10); // Profiles per page
  const [dataCount, setDataCount] = useState<number>(0); // Total number of pages
  const { teamId } = useContext(TeamRouteContext);
  const [filter, setFilter] = useState({
    name: '',
    description: '',
    team_id: '',
    created_at__gte: '',
    created_at__lte: '',
    updated_at__gte: '',
    updated_at__lte: '',
    tlp_levels: []
  })
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<string>('name');
  const navigate = useNavigate()

  const loadDossier = async () => {
    if (!id) return
    try {
      const res = await fetchDossier(teamId, id);
      setDossier(res.data)
    } catch(err) {
      if(err?.response?.status == 404) {
        return navigate(URLS.notFound())
      }
      throw err
    }
  }

  const loadReports = async (pageNumber: number) => {
    if (!id) return
    setLoading(true)
    const res = await fetchDossierReports(teamId, id, filter, pageNumber + 1);
    setReports(res.data.results)
    setDataCount(res.data.count);
    setPageSize(10)
    setLoading(false)
  };

  useEffect(() => { loadDossier(); }, [teamId, id]);

  // Load data on initial render and when the page changes
  useEffect(() => { loadReports(page); }, [page, teamId]);

  const handlePageChange = (event: unknown, value: number) => {
    setPage(value); // Set new page number
  };

  const getReportDetailUrl = (report: Report) => {
    if(teamId) return URLS.teamReportDetail(teamId, report.id)
    return URLS.staffReportDetail(report.id)
  }

  useEffect(() => {
    document.title = 'Dossier | Stixify Web'
  }, [])

  const handleSortChange = (field: string) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    setPage(0);
    loadReports(0)
  };

  const initRemoveReport = (reportId: string) => {
    setOpenRemoveReportDialog(true)
    setSelectedReportId(reportId)
  }

  const removeReport = async () => {
    if (!teamId || !selectedReportId || !id) return
    await removeReportFromDossier(teamId, [id], selectedReportId)
    setOpenRemoveReportDialog(false)
    loadReports(page)
  }

  const initDeleteDossier = () => {
    setDeleteDialogOpen(true)
  }

  return (
    <Box p={4}>
      <Box sx={{ display: 'flex' }}>
        <Typography variant='h4'>Dossier: {dossier?.name}</Typography>
        {teamId && <Link to={URLS.teamEditDossier(teamId, id)}>
          <Button sx={{ marginLeft: '5rem', textTransform: 'uppercase' }} variant="contained" color="primary">
            Edit
          </Button>
        </Link>}
        {teamId && <Button onClick={initDeleteDossier} sx={{ marginLeft: '5rem', textTransform: 'uppercase' }} variant="contained" color="error">
          Delete
        </Button>}
      </Box>

      <Table>
        <TableRow>
          <TableCell>Created By</TableCell>
          <TableCell>{dossier?.team.name}({dossier?.team.id})</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Created At</TableCell>
          <TableCell>{getDateString(dossier?.created_at || '')}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Updated At</TableCell>
          <TableCell>{getDateString(dossier?.updated_at || '')}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Report Count</TableCell>
          <TableCell>{dossier?.report_count}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Labels</TableCell>
          <TableCell>{dossier?.labels.join(',')}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>TLP Level</TableCell>
          <TableCell>{dossier?.tlp_level}</TableCell>
        </TableRow>
      </Table>
      <Table>
        <TableHead>
          <TableCell onClick={() => handleSortChange('name')} style={{ cursor: 'pointer' }}>            Report Title
            {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </TableCell>
          {teamId && <TableCell>Owned by this Team</TableCell>}
          <TableCell>Report created</TableCell>
          <TableCell onClick={() => handleSortChange('tlp_level')} style={{ cursor: 'pointer' }}>            TLP Level
            {sortField === 'tlp_level' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </TableCell>
          <TableCell onClick={() => handleSortChange('confidence')} style={{ cursor: 'pointer' }}>            TLP Level
            Confidence score
            {sortField === 'confidence' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </TableCell>
          <TableCell>Created By </TableCell>
          <TableCell>Labels</TableCell>
          <TableCell></TableCell>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6}>
                <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
              </TableCell>
            </TableRow>
          ) : (<>
            {reports.length === 0 && (
              <TableRow><TableCell sx={{ textAlign: 'center' }} colSpan={6}>No reports found</TableCell></TableRow>
            )}
            {reports.map((report) => (
              <TableRow key={report.id}>
                <TableCell>
                  <Link to={getReportDetailUrl(report)}>{report.stixify_file_metadata.name}</Link></TableCell>
                {teamId && <TableCell>{report.team_id == teamId ? 'True' : 'False'}</TableCell>}
                <TableCell>{getDateString(report?.created_at || '')}</TableCell>
                <TableCell>
                  {report.stixify_file_metadata.tlp_level}
                </TableCell>
                <TableCell>
                  {report.stixify_file_metadata.confidence}
                </TableCell>
                <TableCell>{report.stixify_file_metadata.identity.id}</TableCell>
                <TableCell>{report.stixify_file_metadata.labels.join(', ')}</TableCell>
                <TableCell>
                  {teamId && <Button variant='contained' color='error' onClick={() => initRemoveReport(report.id)} >Remove</Button>}
                </TableCell>
              </TableRow>
            ))}
          </>
          )}
        </TableBody>
      </Table>

      {/* Pagination controls */}
      <TablePagination
        component="div"
        count={dataCount}
        page={page}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        color="primary"
        rowsPerPageOptions={[]}
        labelRowsPerPage={""} // Hide the rows per page label
        style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
      />
      <DeleteDialog open={openRemoveReportDialog} onClose={() => setOpenRemoveReportDialog(false)} onConfirm={removeReport} />
      {dossier && teamId && <ConfirmDeleteDialog teamId={teamId} dossier={dossier} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />}
    </Box>
  );
};

export default DossierDetail;
